/*
===============================================================================================================
                                 Copyright 2023-2024, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { LocaleContext } from '@/contexts/locale-context';
import InlineEditableText from '@/components/inline-editable-text/inline-editable-text';
import { isNil } from 'lodash';

/**
 * Component that mimics the look and feel of a text component but uses inline Editable text components
 * so that the user is able to edi tht values.
 * @param {string} inputSelectorId - DOM id, used to identify this element
 * @param {func} onChangeTitle - Called when the title is saved
 * @param {func} onChangeInput - Called when the placeholder is saved
 * @param {func} onChangeTooltip - Called when the tooltip is saved
 * @param {number} inputModeWidth - Number value for the max widith of the input component
 * @param {string} defaultTitle - The text content of the title
 * @param {string} defaultPlaceholder - The text content of the placeholder
 * @param {string} defaultTooltip - The text content of the tooltip
 * @param {string} defaultResetValueTitle - A text string to display instead of an empty title when resetting the value
 * @param {string} defaultResetValuePlaceholder - A text string to display instead of an empty title when resetting the value
 * @param {boolean} showAsRequired - Whether or not should be shown as a required field
 * @param {shape} titleRules - The validation rules object of the title
 * @param {shape} placeholderRules - The validation rules object of the placeholder
 * @param {shape} tooltipConfig - The 'params' object of the tooltip field in config
 * @param {shape} titleProps - The extra props of the title field in config
 * @param {shape} placeholderProps - The extra props of the placeholder field in config
 */
export default function EditableTextInput({
  inputSelectorId,
  onChangeTitle,
  onChangeInput,
  onChangeTooltip,
  defaultTitle,
  defaultPlaceholder,
  defaultTooltip,
  defaultResetValueTitle,
  defaultResetValuePlaceholder,
  titleRules,
  placeholderRules,
  tooltipConfig,
  titleProps,
  placeholderProps,
  showAsRequired,
}) {
  const { translate } = useContext(LocaleContext);
  const [wrapperWidth, setWrapperWidth] = useState(0);

  const inputWrapper = useRef();

  useEffect(() => {
    setWrapperWidth(inputWrapper.current.offsetWidth);
  }, []);

  return (
    <>
      <div className="editable-text-input-wrapper" id={inputSelectorId} ref={inputWrapper}>
        <div className="title">
          <InlineEditableText
            ariaLabel={titleProps.ariaLabel}
            inputSelectorId={`${inputSelectorId}-title`}
            onChange={onChangeTitle}
            onChangeTooltip={onChangeTooltip}
            fontSize="var(--font-size-H2)"
            defaultValue={defaultTitle}
            defaultValueTooltip={defaultTooltip}
            defaultResetValue={defaultResetValueTitle}
            readOnly={titleProps.readOnly}
            textAreaMode={titleProps.textAreaMode}
            tooltipConfig={tooltipConfig}
            maxLength={titleRules.maxLength}
            externalWrapperWidth={wrapperWidth}
            showAsRequired={showAsRequired}
            validationRules={{
              onChange: titleRules.onChange,
              onBlur: titleRules.onBlurValidationRules,
              onSubmit: titleRules.onSubmit,
              requiredError: translate(titleRules.requiredError),
              onChangeErrors: titleRules.onChangeErrors,
              onBlurErrors: titleRules.onBlurValidationErrors,
              submitErrors: titleRules.submitErrors,
              tooLongError: translate(titleRules.tooLongError),
            }}
          />
        </div>
        <div className="input">
          <InlineEditableText
            ariaLabel={placeholderProps.ariaLabel}
            inputSelectorId={`${inputSelectorId}-input`}
            onChange={onChangeInput}
            defaultValue={defaultPlaceholder}
            defaultResetValue={defaultResetValuePlaceholder}
            inputMode={!isNil(placeholderProps.inputMode) ? placeholderProps.inputMode : true}
            textDisabled={!isNil(placeholderProps.textDisabled) ? placeholderProps.textDisabled : true}
            checkboxMode={placeholderProps.checkboxMode}
            asLink={placeholderProps.asLink}
            calenderIcon={placeholderProps.calenderIcon}
            readOnly={placeholderProps.readOnly}
            textAreaMode={placeholderProps.textAreaMode}
            minHeight={placeholderProps.minHeight}
            maxLength={placeholderRules.maxLength}
            externalWrapperWidth={wrapperWidth}
            maxWidth={placeholderProps.maxWidth}
            validationRules={{
              onChange: placeholderRules.onChange,
              onBlur: placeholderRules.onBlurValidationRules,
              onSubmit: placeholderRules.onSubmit,
              requiredError: translate(placeholderRules.requiredError),
              onChangeErrors: placeholderRules.onChangeErrors,
              onBlurErrors: placeholderRules.onBlurValidationErrors,
              submitErrors: placeholderRules.submitErrors,
              tooLongError: translate(placeholderRules.tooLongError),
            }}
          />
        </div>
      </div>
      <style jsx>
        {`

          .editable-text-input-wrapper {
            display: flex;
            flex-direction: column;
            width: 100%;
          }

        `}
      </style>
    </>
  );
}

EditableTextInput.defaultProps = {
  inputSelectorId: null,
  onChangeTitle: () => {},
  onChangeInput: () => {},
  onChangeTooltip: () => {},
  defaultTitle: null,
  defaultPlaceholder: null,
  defaultTooltip: null,
  defaultResetValueTitle: null,
  defaultResetValuePlaceholder: null,
  showAsRequired: false,
  titleProps: {
    readOnly: false,
    textAreaMode: false,
  },
  placeholderProps: {
    readOnly: false,
    textAreaMode: false,
    minHeight: null,
    maxWidth: null,
    calenderIcon: false,
    checkboxMode: false,
    asLink: false,
    textDisabled: true,
    inputMode: true,
  },
  titleRules: {
    onChangeValidationRules: null,
    onBlurValidationRules: null,
    onSubmitValidationRules: null,
    requiredError: null,
    onChangeValidationErrors: null,
    onBlurValidationErrors: null,
    onSubmitValidationErrors: null,
    minLength: null,
    maxLength: null,
    tooShortError: null,
    tooLongError: null,
  },
  placeholderRules: {
    onChangeValidationRules: null,
    onBlurValidationRules: null,
    onSubmitValidationRules: null,
    requiredError: null,
    onChangeValidationErrors: null,
    onBlurValidationErrors: null,
    onSubmitValidationErrors: null,
    minLength: null,
    maxLength: null,
    tooShortError: null,
    tooLongError: null,
  },
  tooltipConfig: {},
};

EditableTextInput.propTypes = {
  inputSelectorId: PropTypes.string,
  onChangeTitle: PropTypes.func,
  onChangeInput: PropTypes.func,
  onChangeTooltip: PropTypes.func,
  defaultTitle: PropTypes.string,
  defaultPlaceholder: PropTypes.string,
  defaultTooltip: PropTypes.string,
  defaultResetValueTitle: PropTypes.string,
  defaultResetValuePlaceholder: PropTypes.string,
  showAsRequired: PropTypes.bool,
  titleProps: PropTypes.shape({
    readOnly: PropTypes.bool,
    textAreaMode: PropTypes.bool,
    ariaLabel: PropTypes.string,
  }),
  placeholderProps: PropTypes.shape({
    readOnly: PropTypes.bool,
    textAreaMode: PropTypes.bool,
    minHeight: PropTypes.string,
    maxWidth: PropTypes.number,
    placeholderProps: PropTypes.string,
    calenderIcon: PropTypes.bool,
    checkboxMode: PropTypes.bool,
    asLink: PropTypes.bool,
    textDisabled: PropTypes.bool,
    inputMode: PropTypes.bool,
    ariaLabel: PropTypes.string,
  }),
  titleRules: PropTypes.shape({
    onSubmit: PropTypes.arrayOf(PropTypes.string),
    onBlurValidationRules: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.arrayOf(PropTypes.string),
    requiredError: PropTypes.string,
    submitErrors: PropTypes.arrayOf(PropTypes.string),
    onChangeErrors: PropTypes.arrayOf(PropTypes.string),
    onBlurValidationErrors: PropTypes.arrayOf(PropTypes.string),
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    tooShortError: PropTypes.string,
    tooLongError: PropTypes.string,
  }),
  placeholderRules: PropTypes.shape({
    onSubmit: PropTypes.arrayOf(PropTypes.string),
    onBlurValidationRules: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.arrayOf(PropTypes.string),
    requiredError: PropTypes.string,
    submitErrors: PropTypes.arrayOf(PropTypes.string),
    onChangeErrors: PropTypes.arrayOf(PropTypes.string),
    onBlurValidationErrors: PropTypes.arrayOf(PropTypes.string),
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    tooShortError: PropTypes.string,
    tooLongError: PropTypes.string,
  }),
  tooltipConfig: PropTypes.shape({
    enabled: PropTypes.bool,
    defaultResetValue: PropTypes.string,
    onSubmitValidationRules: PropTypes.arrayOf(PropTypes.string),
    onChangeValidationRules: PropTypes.arrayOf(PropTypes.string),
    maxLength: PropTypes.number,
    tooLongError: PropTypes.string,
  }),
};
